import { useTranslation } from "react-i18next"
import { useFragment } from "react-relay"
import { t } from "i18next"
import { Tag } from "@/components/ui/tag"
import { HealthDataCardSession } from "@/modules/patients/detail/health-information"
import { BodyMeasurementsDrawer } from "@/modules/patients/detail/health-information/body-measurement/body-measurements-drawer"
import { isLatestData } from "@/modules/patients/detail/health-information/body-measurement/lib/is-latest-data"
import { useGetHealthInformationVerification } from "@/modules/patients/detail/health-information/hooks/useGetHealthInformationVerification"
import { useHealthInformationInContextAppointment } from "@/modules/patients/detail/health-information/hooks/useHealthInformationCanVerify"
import { PatientBodyMeasurementsOutputFragment$key } from "@/modules/patients/services/__generated__/PatientBodyMeasurementsOutputFragment.graphql"
import { PatientBodyMeasurementsOutputFragment } from "@/modules/patients/services/PatientBodyMeasurementsOutputFragment"
import { usePatientBodyMeasurement } from "@/modules/patients/services/usePatientBodyMeasurement"
import { useDrawerStore } from "@/stores/useDrawerStore"
import { Country } from "@/types/__generated__/union-types"

interface BodyMeasurementBodyProps extends React.HTMLAttributes<HTMLDivElement> {
  country: Country
  patientId: string
}

interface BodyMeasurementCardBodyProps extends React.HTMLAttributes<HTMLDivElement> {
  bodyMassIndex: number | null
  height: number | null
  weight: number
}

const BodyMeasurementCardBody = ({ bodyMassIndex, height, weight }: BodyMeasurementCardBodyProps) => {
  const { t } = useTranslation()

  return (
    <div className="flex flex-wrap items-center gap-2">
      <Tag
        key="height"
        type="neutral"
        label={
          height !== null ? `${height} ${t("patient.healthInformation.bodyMeasurements.measurements.heightUnit")}` : "-"
        }
        data-testid="selected-height-value"
      />
      <Tag
        key="weight"
        type="neutral"
        label={`${weight} ${t("patient.healthInformation.bodyMeasurements.measurements.weightUnit")}`}
        data-testid="selected-weight-value"
      />
      <Tag
        key="bodyMassIndex"
        type="neutral"
        label={`${t("patient.healthInformation.bodyMeasurements.measurements.bmi")}: ${bodyMassIndex ?? "-"} `}
        data-testid="selected-bmi-value"
      />
    </div>
  )
}

const BodyMeasurement = ({ country, patientId }: BodyMeasurementBodyProps) => {
  const openDrawer = useDrawerStore((state) => state.openDrawer)
  const drawerName = useDrawerStore((state) => state.drawerName)
  const dataRef = usePatientBodyMeasurement(patientId, country)

  let patientBodyMeasurements = useFragment<PatientBodyMeasurementsOutputFragment$key>(
    PatientBodyMeasurementsOutputFragment,
    dataRef?.edges ?? null,
  )
  patientBodyMeasurements = patientBodyMeasurements
    ? patientBodyMeasurements.toSorted((a, b) => {
        return Number(new Date(b.node.date)) - Number(new Date(a.node.date))
      })
    : null
  const newestPatientBodyMeasurement = patientBodyMeasurements?.at(0)?.node

  const { isVerified } = useGetHealthInformationVerification(patientId, country, "BODY_MEASUREMENT")
  const validAppointment = useHealthInformationInContextAppointment()
  const showAsUnverified = validAppointment && !isVerified && !isLatestData(newestPatientBodyMeasurement?.date)

  return (
    <HealthDataCardSession
      canVerify={showAsUnverified}
      sectionName="body-measurement"
      analyticsSectionName="healthInformation_bodyMeasurements"
      disabled={drawerName === "BodyMeasurementsDrawer"}
      title={t("patient.healthInformation.bodyMeasurements.title")}
      editHandler={() => {
        openDrawer(() => <BodyMeasurementsDrawer validAppointment={validAppointment} />, "BodyMeasurementsDrawer")
      }}>
      {newestPatientBodyMeasurement && (
        <BodyMeasurementCardBody
          bodyMassIndex={newestPatientBodyMeasurement.bodyMassIndex}
          height={newestPatientBodyMeasurement.height}
          weight={newestPatientBodyMeasurement.weight}
        />
      )}
    </HealthDataCardSession>
  )
}

export { BodyMeasurement }
